/**
 * 电商套图 · 蒙版局部编辑弹窗
 *
 * 在成品图上涂抹需要修改的区域，导出为 OpenAI edits 语义的蒙版 PNG：
 * 涂抹区域 alpha=0（透明 = 需要重绘），其余区域不透明。
 * 生成走平台 inpaint 链路（createSuiteShotEditJob）。
 */
import { useRef, useState } from 'react';
import { Eraser, Loader2, Sparkles, X } from 'lucide-react';
import toast from '../../../components/Toast';
import { suiteTheme } from '../theme';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';

export function MaskEditModal({
  image,
  displayName,
  submitting,
  onClose,
  onSubmit,
}: {
  image: string;
  displayName: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: { instruction: string; maskImage: string }) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const paintingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [instruction, setInstruction] = useState('');
  const [brushSize, setBrushSize] = useState(36);
  const [painted, setPainted] = useState(false);

  const setupCanvas = (img: HTMLImageElement) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const canvasPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
      radius: (brushSize * scaleX) / 2,
    };
  };

  const strokeTo = (point: { x: number; y: number; radius: number }) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const last = lastPointRef.current;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = point.radius * 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';
    if (last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(point.x, point.y, point.radius, 0, Math.PI * 2);
      ctx.fillStyle = '#000';
      ctx.fill();
    }
    lastPointRef.current = { x: point.x, y: point.y };
    if (!painted) setPainted(true);
  };

  const clearMask = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    lastPointRef.current = null;
    setPainted(false);
  };

  const handleSubmit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!instruction.trim()) {
      toast.error('请填写编辑要求');
      return;
    }
    if (!painted) {
      toast.error('请先在图片上涂抹要修改的区域');
      return;
    }
    onSubmit({ instruction: instruction.trim(), maskImage: canvas.toDataURL('image/png') });
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <button aria-label="关闭" className="absolute inset-0 bg-zinc-950/75 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden ${suiteTheme.card}`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-semibold text-zinc-800">局部编辑 · {displayName}</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 transition hover:text-zinc-700" aria-label="关闭">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-zinc-500">
              画笔大小
              <input
                type="range"
                min={12}
                max={120}
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-32 accent-blue-200"
              />
            </label>
            <button type="button" className={suiteTheme.ghostBtn} onClick={clearMask}>
              <Eraser className="h-3.5 w-3.5" />
              清除蒙版
            </button>
            <span className="text-xs text-zinc-500">涂抹的区域将被重绘，其余部分保持不变</span>
          </div>

          <div className="relative mx-auto max-w-xl overflow-hidden rounded-2xl border border-slate-200">
            <img
              src={resolveBrowserMediaUrl(image)}
              alt={displayName}
              className="block w-full select-none"
              onLoad={(e) => setupCanvas(e.currentTarget)}
              draggable={false}
            />
            <canvas
              ref={canvasRef}
              className="absolute inset-0 h-full w-full cursor-crosshair touch-none opacity-60"
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                paintingRef.current = true;
                lastPointRef.current = null;
                const point = canvasPoint(e);
                if (point) strokeTo(point);
              }}
              onPointerMove={(e) => {
                if (!paintingRef.current) return;
                const point = canvasPoint(e);
                if (point) strokeTo(point);
              }}
              onPointerUp={() => {
                paintingRef.current = false;
                lastPointRef.current = null;
              }}
              onPointerLeave={() => {
                paintingRef.current = false;
                lastPointRef.current = null;
              }}
            />
          </div>

          <div>
            <label className={suiteTheme.label}>编辑要求（只描述要改什么）</label>
            <textarea
              className={`${suiteTheme.textarea} min-h-[72px]`}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="例如：把背景的杯子换成绿植；修正左侧文字为「限量发售」"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button type="button" className={suiteTheme.ghostBtn} onClick={onClose} disabled={submitting}>
            取消
          </button>
          <button type="button" className={suiteTheme.primaryBtn} onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            提交局部编辑
          </button>
        </div>
      </div>
    </div>
  );
}
